import { CloseOutlined, EyeOutlined } from '@ant-design/icons'
import { useState } from 'react'
import styled from 'styled-components'
import { PopoverModal } from 'sutra/components/PopoverModal/PopoverModal'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { Role } from 'types/hub-role'

const ROLE_LABELS: Record<Role, string> = {
  [Role.Guest]: 'Guest',
  [Role.Member]: 'Member',
  [Role.Admin]: 'Admin',
  [Role.Owner]: 'Owner',
}

export interface ViewAsRoleBannerProps {
  /** The previewed role, or `null` when no preview is active (banner hidden). */
  readonly previewedHubRole: Role | null
  /**
   * Navigate to the view-as control (the access settings page). Fired when the
   * banner body (or the collapsed badge) is clicked.
   */
  readonly onNavigate: () => void
  /** Exit the preview. Called only after the user confirms the exit modal. */
  readonly onExit: () => void
  /** Render the compact (icon-only) variant for the collapsed sidebar. */
  readonly isCollapsed: boolean
}

/**
 * Sidebar banner shown while an owner is previewing a role. Renders "viewing
 * as {role}"; clicking the banner deep-links to the view-as control, and the X
 * opens a confirm modal that exits the preview only on confirm. Hidden when no
 * preview is active. Degrades to an icon-only badge in the collapsed-nav state.
 */
export const ViewAsRoleBanner = ({
  previewedHubRole,
  onNavigate,
  onExit,
  isCollapsed,
}: ViewAsRoleBannerProps): JSX.Element | null => {
  const [confirmOpen, setConfirmOpen] = useState(false)

  if (previewedHubRole === null) return null

  const label = ROLE_LABELS[previewedHubRole]

  const confirmModal = (
    <PopoverModal
      isOpen={confirmOpen}
      title="Exit view-as mode?"
      okText="Exit"
      cancelText="Cancel"
      onClose={() => setConfirmOpen(false)}
      onOk={() => {
        setConfirmOpen(false)
        onExit()
      }}
      width={520}
      content={<>Stop previewing as {label} and return to your own role?</>}
    />
  )

  if (isCollapsed) {
    return (
      <>
        <Tooltip content={`Viewing as ${label} — click to manage`} side="right">
          <CollapsedBadge
            data-testid="view-as-role-banner"
            onClick={onNavigate}
            aria-label={`Viewing as ${label}, manage preview`}
          >
            <EyeOutlined />
          </CollapsedBadge>
        </Tooltip>
        {confirmModal}
      </>
    )
  }

  return (
    <>
      <Banner data-testid="view-as-role-banner">
        <BannerText
          data-testid="view-as-role-banner-navigate"
          onClick={onNavigate}
          aria-label={`Viewing as ${label}, go to view-as control`}
        >
          <EyeOutlined /> Viewing as {label}
        </BannerText>
        <ExitButton
          data-testid="view-as-role-banner-exit"
          onClick={() => setConfirmOpen(true)}
          aria-label="Exit preview"
        >
          <CloseOutlined />
        </ExitButton>
      </Banner>
      {confirmModal}
    </>
  )
}

const Banner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 6px 10px;
  border-radius: 6px;
  background: #fef3c7;
  color: #92400e;
  font-size: 12px;
  font-weight: 600;
`

const BannerText = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ExitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #92400e;
  cursor: pointer;
  padding: 2px;
  flex-shrink: 0;
`

const CollapsedBadge = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #fef3c7;
  color: #92400e;
  cursor: pointer;
`
